import { useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Box, MoreVertical, Edit, Trash2, X, Router, Shield, Globe, Key, Cloud, Wifi, Server, HardDrive, Phone, FileText, Lock, Building2 } from 'lucide-react';
import { documentationAPI, adminCompanyAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import { isInternalRole } from '../../utils/roles.js';

// Icon mapping for asset types
const TYPE_ICONS = {
  'network-device': Router,
  'ssl-certificate': Shield,
  'domain': Globe,
  'software-license': Key,
  'cloud-service': Cloud,
  'lan': Wifi,
  'backup': HardDrive,
  'email': FileText,
  'active-directory': Server,
  'security': Shield,
  'voice-pbx': Phone,
  default: Box
};

function getTypeIcon(slug) {
  return TYPE_ICONS[slug] || TYPE_ICONS.default;
}

// Visibility badge
function VisibilityBadge({ visibility }) {
  const config = {
    private: { icon: Lock, label: 'Internal', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    company: { icon: Building2, label: 'Company', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    public: { icon: Globe, label: 'Public', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  const { icon: Icon, label, className } = config[visibility] || config.private;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// Asset type card for sidebar
function AssetTypeCard({ type, isSelected, onClick }) {
  const Icon = getTypeIcon(type.slug);

  return (
    <button
      onClick={() => onClick(type)}
      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
        isSelected
          ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400 border border-brand-200 dark:border-brand-800'
          : 'hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 border border-transparent'
      }`}
    >
      <div className={`p-2 rounded-lg ${isSelected ? 'bg-brand-100 dark:bg-brand-900/50' : 'bg-neutral-100 dark:bg-neutral-700'}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{type.name}</p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{type.asset_count || 0} assets</p>
      </div>
    </button>
  );
}

// Dynamic field renderer for asset field values
function FieldValue({ field, value }) {
  if (!value) return <span className="text-neutral-400">-</span>;

  switch (field.type) {
    case 'url':
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-700 truncate block">
          {value}
        </a>
      );
    case 'email':
      return (
        <a href={`mailto:${value}`} className="text-brand-600 hover:text-brand-700">
          {value}
        </a>
      );
    case 'checkbox':
      return value ? 'Yes' : 'No';
    case 'date':
      return new Date(value).toLocaleDateString();
    default:
      return <span className="truncate">{value}</span>;
  }
}

// Asset card
function AssetCard({ asset, type, isInternal, onEdit, onDelete }) {
  const [showMenu, setShowMenu] = useState(false);
  const Icon = getTypeIcon(type?.slug);
  const fields = type?.field_schema || [];

  return (
    <div className="group p-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-brand-300 dark:hover:border-brand-600 hover:shadow-md transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-neutral-100 dark:bg-neutral-700 rounded-lg">
            <Icon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
          </div>
          <div>
            <Link
              to={`/documentation/flexible-assets/${asset.id}`}
              className="font-medium text-neutral-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400"
            >
              {asset.name}
            </Link>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{type?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isInternal && <VisibilityBadge visibility={asset.visibility} />}
          {isInternal && (
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-opacity"
              >
                <MoreVertical className="w-4 h-4 text-neutral-500" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg py-1 min-w-[120px]">
                    <button
                      onClick={() => { setShowMenu(false); onEdit(asset); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => { setShowMenu(false); onDelete(asset); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Show first few field values */}
      <div className="space-y-1">
        {fields.slice(0, 4).map(field => {
          const value = asset.field_values?.[field.name];
          if (!value) return null;
          return (
            <div key={field.name} className="flex items-center gap-2 text-sm">
              <span className="text-neutral-500 dark:text-neutral-400">{field.label}:</span>
              <span className="text-neutral-900 dark:text-white truncate">
                <FieldValue field={field} value={value} />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Asset modal for create/edit
function AssetModal({ asset, type, companyId, isInternal, onClose }) {
  const queryClient = useQueryClient();
  const fields = type?.field_schema || [];

  const [formData, setFormData] = useState({
    name: asset?.name || '',
    fieldValues: asset?.field_values || {},
    visibility: asset?.visibility || 'private',
    companyId: asset?.company_id || companyId || '',
  });
  const [error, setError] = useState(null);

  // Fetch companies for internal staff
  const { data: companiesData } = useQuery({
    queryKey: ['admin', 'companies', 'list'],
    queryFn: () => adminCompanyAPI.list({ limit: 500 }),
    enabled: isInternal,
  });
  const companies = companiesData?.data?.companies || [];

  const createMutation = useMutation({
    mutationFn: (data) => documentationAPI.createFlexibleAsset({
      assetTypeId: type.id,
      companyId: data.companyId,
      name: data.name,
      fieldValues: data.fieldValues,
      visibility: data.visibility,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'flexible-assets'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to create asset'),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => documentationAPI.updateFlexibleAsset(asset.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'flexible-assets'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to update asset'),
  });

  const handleFieldChange = (fieldName, value) => {
    setFormData({
      ...formData,
      fieldValues: { ...formData.fieldValues, [fieldName]: value }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!asset && !formData.companyId) {
      setError('Company is required');
      return;
    }
    if (asset) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 bg-white dark:bg-neutral-800">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {asset ? `Edit ${type.name}` : `New ${type.name}`}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* Company selector for internal staff */}
          {isInternal && !asset && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Company *
              </label>
              <select
                value={formData.companyId}
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value ? parseInt(e.target.value) : '' })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                required
              >
                <option value="">Select a company...</option>
                {companies.map(company => (
                  <option key={company.id} value={company.cw_company_id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Dynamic fields */}
          {fields.map(field => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                {field.label} {field.required && '*'}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  value={formData.fieldValues[field.name] || ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 resize-none"
                />
              ) : field.type === 'select' ? (
                <select
                  value={formData.fieldValues[field.name] || ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">Select...</option>
                  {field.options?.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : field.type === 'checkbox' ? (
                <input
                  type="checkbox"
                  checked={formData.fieldValues[field.name] || false}
                  onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                  className="w-4 h-4 text-brand-600 border-neutral-300 rounded focus:ring-brand-500"
                />
              ) : (
                <input
                  type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
                  value={formData.fieldValues[field.name] || ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                />
              )}
            </div>
          ))}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Visibility</label>
            <select
              value={formData.visibility}
              onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
            >
              <option value="private">Internal Only</option>
              <option value="company">Visible to Company</option>
              <option value="public">Public</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white rounded-lg"
            >
              {isLoading ? 'Saving...' : asset ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete confirmation
function DeleteConfirmModal({ asset, onClose }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: () => documentationAPI.deleteFlexibleAsset(asset.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'flexible-assets'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to delete'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">Delete Asset?</h2>
        <p className="text-neutral-600 dark:text-neutral-400 mb-4">
          Are you sure you want to delete "{asset.name}"?
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300">
            Cancel
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FlexibleAssetsPage() {
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [assetToEdit, setAssetToEdit] = useState(null);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const { user } = useAuthStore();

  const isInternal = isInternalRole(user?.role);
  const companyId = user?.cw_company_id;

  // Fetch asset types
  const { data: typesData } = useQuery({
    queryKey: ['documentation', 'asset-types'],
    queryFn: () => documentationAPI.listAssetTypes(),
  });

  // Fetch assets for selected type
  const debouncedSearch = useDebounce(search, 300);
  const { data: assetsData, isLoading } = useQuery({
    queryKey: ['documentation', 'flexible-assets', { typeId: selectedType?.id, search: debouncedSearch, companyId: isInternal ? undefined : companyId }],
    queryFn: () => documentationAPI.listFlexibleAssets({
      assetTypeId: selectedType?.id,
      search: debouncedSearch || undefined,
      companyId: isInternal ? undefined : companyId,
      limit: 50
    }),
    enabled: !!selectedType,
  });

  const types = typesData?.data?.types || [];
  const assets = assetsData?.data?.assets || [];

  // Auto-select first type
  if (!selectedType && types.length > 0) {
    setSelectedType(types[0]);
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Flexible Assets</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Structured documentation for LAN, backup, email, and more
          </p>
        </div>
        {isInternal && selectedType && (
          <button
            onClick={() => { setAssetToEdit(null); setShowModal(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New {selectedType.name}
          </button>
        )}
      </div>

      <div className="flex gap-6">
        {/* Sidebar - Asset Types */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <h3 className="font-medium text-neutral-900 dark:text-white mb-3">Asset Types</h3>
            <div className="space-y-2">
              {types.map(type => (
                <AssetTypeCard
                  key={type.id}
                  type={type}
                  isSelected={selectedType?.id === type.id}
                  onClick={setSelectedType}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Mobile type selector */}
          <div className="lg:hidden mb-4">
            <select
              value={selectedType?.id || ''}
              onChange={(e) => {
                const type = types.find(t => t.id === e.target.value);
                setSelectedType(type);
              }}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white"
            >
              {types.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${selectedType?.name || 'assets'}...`}
                className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </div>

          {/* Assets Grid */}
          {!selectedType ? (
            <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <Box className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">Select an asset type</h3>
              <p className="text-neutral-600 dark:text-neutral-400">Choose an asset type from the sidebar to view assets</p>
            </div>
          ) : isLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="p-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 animate-pulse">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 bg-neutral-200 dark:bg-neutral-700 rounded-lg" />
                    <div className="flex-1">
                      <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-2/3 mb-1" />
                      <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : assets.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {assets.map(asset => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  type={selectedType}
                  isInternal={isInternal}
                  onEdit={(a) => { setAssetToEdit(a); setShowModal(true); }}
                  onDelete={setAssetToDelete}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <Box className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
                {search ? 'No assets found' : `No ${selectedType.name} assets yet`}
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                {search ? 'Try adjusting your search' : isInternal ? 'Create your first asset to get started' : 'No assets to display'}
              </p>
              {isInternal && !search && (
                <button
                  onClick={() => { setAssetToEdit(null); setShowModal(true); }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New {selectedType.name}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showModal && selectedType && (
        <AssetModal
          asset={assetToEdit}
          type={selectedType}
          companyId={companyId}
          isInternal={isInternal}
          onClose={() => { setShowModal(false); setAssetToEdit(null); }}
        />
      )}

      {assetToDelete && (
        <DeleteConfirmModal
          asset={assetToDelete}
          onClose={() => setAssetToDelete(null)}
        />
      )}
    </div>
  );
}
